/**
 * Deputy BOLOs: put one out (a white Bronco, a person), it's on everyone's Home; the deputy who put it out is asked
 * every 5 days whether it's still needed. Located or canceled takes it down.
 */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, fileBase64, when } from './common.tsx';

const STATUS: Record<string, string> = { active: 'Active', located: 'Located', canceled: 'Canceled' };
const TONE: Record<string, 'warn' | 'ok' | 'neutral'> = { active: 'warn', located: 'ok', canceled: 'neutral' };
export const vehicleText = (v: any) => [v?.color, v?.year, v?.make, v?.model].filter(Boolean).join(' ') + (v?.plate ? ` · ${v.plate}${v.state ? ` (${v.state})` : ''}` : '');
const personText = (p: any) => [p?.name, p?.dob && `DOB ${p.dob}`, [p?.race, p?.sex].filter(Boolean).join('/')].filter(Boolean).join(' · ');
export const boloLine = (b: any) => (b.kind === 'vehicle' ? vehicleText(b.vehicle) : b.kind === 'person' ? personText(b.person) : '') || b.details || '';

export function BolosPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/field-bolos?status=${status}&q=${encodeURIComponent(q)}`), [status, q]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Patrol"
        title="BOLOs"
        sub="Be on the lookout. Anyone can put one out; it shows on everyone’s Home until it’s located or canceled."
        actions={
          r.data?.canPost && (
            <button className="btn primary" onClick={() => navigate(at('/bolos/new'))}>
              Put out a BOLO
            </button>
          )
        }
      />
      <div className="row gap-sm wrap">
        <div className="seg-ctl" role="tablist" aria-label="Which BOLOs">
          {(
            [
              ['active', 'Active'],
              ['closed', 'Taken down'],
              ['all', 'All'],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
              {l}
            </button>
          ))}
        </div>
        <input type="search" placeholder="Search plate, name, case…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search BOLOs" style={{ maxWidth: 280 }} />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.bolos.length ? (
        <EmptyState icon="bolo" title={status === 'active' ? 'No active BOLOs' : 'None'} />
      ) : (
        <div className="bolo-grid">
          {r.data.bolos.map((b: any) => (
            <button key={b.id} type="button" className="card bolo-card" onClick={() => navigate(at(`/bolos/${b.id}`))}>
              {b.has_photo ? <img src={`/api/field-bolos/${b.id}/photo`} alt="" /> : <div className="bolo-ph">{b.kind === 'vehicle' ? '🚙' : b.kind === 'person' ? '👤' : '!'}</div>}
              <div className="stack xs">
                <div className="row between gap-sm">
                  <strong>{b.title}</strong>
                  <Badge tone={TONE[b.status]} dot>
                    {STATUS[b.status]}
                  </Badge>
                </div>
                <div className="small">{boloLine(b)}</div>
                <div className="small muted">
                  {b.number} · {b.by_name} · {when(b.created_at)}
                  {b.case_number ? ` · case ${b.case_number}` : ''}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function NewBolo() {
  const { navigate } = useRouter();
  const [kind, setKind] = useState<'vehicle' | 'person' | 'other'>('vehicle');
  const [f, setF] = useState<Record<string, string>>({});
  const [v, setV] = useState<Record<string, string>>({ state: 'SC' });
  const [p, setP] = useState<Record<string, string>>({});
  const [photo, setPhoto] = useState<File | null>(null);
  const a = useAction();
  const vin = (k: string, l: string, w?: string) => (
    <label style={w ? { maxWidth: w } : undefined}>
      {l}
      <input value={v[k] ?? ''} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
    </label>
  );
  const pin = (k: string, l: string, type = 'text') => (
    <label>
      {l}
      <input type={type} value={p[k] ?? ''} onChange={(e) => setP({ ...p, [k]: e.target.value })} />
    </label>
  );
  return (
    <div className="ops stack">
      <PageHeader crumb="BOLOs" title="Put out a BOLO" sub="Everyone sees it on their Home. You’ll be asked every 5 days if it’s still needed." />
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const r = await api.post<{ id: string }>('/api/field-bolos', { kind, ...f, vehicle: kind === 'vehicle' ? v : undefined, person: kind === 'person' ? p : undefined, photoBase64: photo ? await fileBase64(photo) : undefined });
            navigate(at(`/bolos/${r.id}`), true);
          });
        }}
      >
        <Card title="What to look for">
          <div className="seg-ctl" role="tablist" aria-label="Kind">
            {(
              [
                ['vehicle', 'Vehicle'],
                ['person', 'Person'],
                ['other', 'Other'],
              ] as const
            ).map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={kind === k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>
                {l}
              </button>
            ))}
          </div>
          <label>
            Headline
            <input required maxLength={160} placeholder={kind === 'vehicle' ? 'White Ford Bronco — left a shooting on Hwy 9' : kind === 'person' ? 'Missing juvenile — Tyler Moss' : 'What everyone should watch for'} value={f.title ?? ''} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </label>
          {kind === 'vehicle' && (
            <div className="grid-3">
              {vin('color', 'Color')}
              {vin('year', 'Year')}
              {vin('make', 'Make')}
              {vin('model', 'Model')}
              {vin('plate', 'Tag')}
              {vin('state', 'Tag state')}
              <label style={{ gridColumn: '1 / -1' }}>
                Damage, stickers, anything that stands out
                <input value={v.marks ?? ''} onChange={(e) => setV({ ...v, marks: e.target.value })} />
              </label>
            </div>
          )}
          {kind === 'person' && (
            <div className="grid-3">
              {pin('name', 'Name')}
              {pin('dob', 'Date of birth', 'date')}
              <label>
                Sex
                <select value={p.sex ?? ''} onChange={(e) => setP({ ...p, sex: e.target.value })}>
                  <option value="">—</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </label>
              <label style={{ gridColumn: '1 / -1' }}>
                Description (height, build, clothing, last seen)
                <input value={p.description ?? ''} onChange={(e) => setP({ ...p, description: e.target.value })} />
              </label>
            </div>
          )}
          <label>
            Details
            <textarea rows={4} value={f.details ?? ''} onChange={(e) => setF({ ...f, details: e.target.value })} placeholder="Why, where it was last seen, what to do if found (stop, hold, just call)…" />
          </label>
          <div className="grid-3">
            <label>
              Case number
              <input value={f.caseNumber ?? ''} onChange={(e) => setF({ ...f, caseNumber: e.target.value })} />
            </label>
            <label>
              If found, contact
              <input value={f.contact ?? ''} onChange={(e) => setF({ ...f, contact: e.target.value })} placeholder="Me, 803-555-…; or dispatch" />
            </label>
            <label>
              Photo
              <input type="file" accept="image/jpeg,image/png" capture="environment" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
            </label>
          </div>
        </Card>
        <ErrorBox error={a.error} />
        <div>
          <button className="btn primary" disabled={a.busy}>
            Put it out
          </button>
        </div>
      </form>
    </div>
  );
}

export function BoloDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/field-bolos/${id}`), [id]);
  const a = useAction();
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { bolo: b, canClose, mine } = r.data;
  const act = (action: string, ask?: string) => {
    const note = ask ? window.prompt(ask) : null;
    if (ask && note === null) return;
    a.run(async () => (await api.post(`/api/field-bolos/${id}/act`, { action, note: note || undefined }), r.reload()));
  };
  const daysUp = Math.floor((Date.now() - +new Date(b.confirmed_at)) / 86_400_000);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="BOLOs"
        title={b.title}
        sub={`${b.number} · put out by ${b.by_name}${b.by_call ? ` (${b.by_call})` : ''} · ${when(b.created_at)}`}
        actions={
          <Badge tone={TONE[b.status]} dot>
            {STATUS[b.status]}
          </Badge>
        }
      />
      {mine && b.status === 'active' && daysUp >= 5 && <Notice tone="warn">This has been up {daysUp} days. Is it still needed?</Notice>}
      {b.status !== 'active' && (
        <Notice tone="info">
          {STATUS[b.status]} {when(b.closed_at)} by {b.closed_by_name}
          {b.closed_note ? ` — ${b.closed_note}` : ''}
        </Notice>
      )}
      <ErrorBox error={a.error} />
      <div className="grid-2-cards">
        <Card title={b.kind === 'vehicle' ? 'Vehicle' : b.kind === 'person' ? 'Person' : 'Details'}>
          {b.has_photo && <img className="bolo-photo" src={`/api/field-bolos/${b.id}/photo`} alt="BOLO photo" />}
          <dl className="kv">
            {b.kind === 'vehicle' && (
              <>
                <dt>Vehicle</dt>
                <dd>{[b.vehicle.color, b.vehicle.year, b.vehicle.make, b.vehicle.model].filter(Boolean).join(' ') || '—'}</dd>
                <dt>Tag</dt>
                <dd className="mono">{b.vehicle.plate ? `${b.vehicle.plate} ${b.vehicle.state ?? ''}` : '—'}</dd>
                <dt>Marks</dt>
                <dd>{b.vehicle.marks ?? '—'}</dd>
              </>
            )}
            {b.kind === 'person' && (
              <>
                <dt>Name</dt>
                <dd>{b.person.name ?? '—'}</dd>
                <dt>DOB / sex</dt>
                <dd>{[b.person.dob, b.person.sex].filter(Boolean).join(' · ') || '—'}</dd>
                <dt>Description</dt>
                <dd>{b.person.description ?? '—'}</dd>
              </>
            )}
            <dt>Case</dt>
            <dd>{b.case_number ?? '—'}</dd>
            <dt>If found</dt>
            <dd>{b.contact ?? `${b.by_name}${b.by_phone ? `, ${b.by_phone}` : ''}`}</dd>
          </dl>
          {b.details && <p className="pre">{b.details}</p>}
        </Card>
        {canClose && (
          <Card title="Still needed?">
            <div className="stack sm">
              <p className="small muted">The deputy who put it out is asked every 5 days. Located or canceled takes it off everyone’s Home.</p>
              <div className="row gap-sm wrap">
                <button className="btn" disabled={a.busy} onClick={() => act('keep')}>
                  Keep it up
                </button>
                <button className="btn primary" disabled={a.busy} onClick={() => act('located', 'Where / how was it located? (optional)')}>
                  Located
                </button>
                <button className="btn ghost" disabled={a.busy} onClick={() => act('cancel', 'Why cancel it? (optional)')}>
                  Cancel the BOLO
                </button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

/** Home: the newest BOLOs, for everyone. */
export function HomeBolos({ bolos }: { bolos: any[] }) {
  const { navigate } = useRouter();
  if (!bolos?.length) return null;
  return (
    <Card title="BOLOs" actions={<a href={at('/bolos')} onClick={(e) => (e.preventDefault(), navigate(at('/bolos')))} className="small">All BOLOs</a>}>
      <ul className="bolo-home">
        {bolos.map((b) => (
          <li key={b.id}>
            <a href={at(`/bolos/${b.id}`)} onClick={(e) => (e.preventDefault(), navigate(at(`/bolos/${b.id}`)))}>
              {b.has_photo && <img src={`/api/field-bolos/${b.id}/photo`} alt="" />}
              <span>
                <strong>{b.title}</strong>
                <span className="small muted"> · {boloLine(b)}</span>
                <span className="small muted block">
                  {b.by_name} · {when(b.created_at)}
                </span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </Card>
  );
}
